import { IImage } from "@faire/types-common-api/indigofair/data/IImage";
import isNil from "lodash/isNil";
import startsWith from "lodash/startsWith";
import qs from "query-string";

import { getDpr, optimizeDimensionsForCacheHits } from "./dpr";
import { logger } from "./private/logger";
import { IOptimizeImageConfig, IOptimizeImageOptions } from "./types";

// Test if given URL is a .tiff url
const isTiff = (url: string): boolean => /\.tiff?($|\?)/.test(url);

// Test if given URL is a fastly/internal faire CDN url
export const isFastly = (url: string): boolean =>
  /cdn\.faire\.com\//.test(url) ||
  /cdn\.faire-stage\.com\//.test(url) ||
  /cdn\.prerelease\.faire\.dev\//.test(url);

/**
 * Useful if different UI handles transformable image vs something else
 */
export const isTransformableImageUrl = (image?: Partial<IImage>): boolean =>
  !!image && !!image.url && !isTiff(image.url) && isFastly(image.url);

const isHexColor = (color: string): boolean => color[0] === "#";

const RESIZE_METHOD_TAG_PREFIX = "cloudinary_crop_";
export const getResizeMethodTag = (
  tags: string[] = []
): IOptimizeImageOptions["resizeMethod"] | undefined => {
  const resizeMethodTag =
    Array.isArray(tags) &&
    tags.find((tag) => startsWith(tag, RESIZE_METHOD_TAG_PREFIX));
  if (resizeMethodTag) {
    return resizeMethodTag.replace(
      new RegExp(`^${RESIZE_METHOD_TAG_PREFIX}`),
      ""
    ) as IOptimizeImageOptions["resizeMethod"];
  }
  return undefined;
};

// eslint-disable-next-line complexity/complexity
export const getFastlyUrl = (
  imageUrl: string,
  options: IOptimizeImageConfig,
  isSSR: boolean
): string => {
  if (!isFastly(imageUrl)) {
    return imageUrl;
  }

  const [pathName, ...queries] = imageUrl.split("?");

  const params = qs.parse(queries.join("?"));

  params.dpr = String(getDpr(options, isSSR));

  if (options.useLossless) {
    params.format = "png";
  } else if (options.maintainTransparency) {
    params.format = null;
  } else if (options.format === "auto") {
    params.format = "auto";
    // Fastly keeps lossless output for PNG/GIF sources under format=auto unless
    // quality < 100 is set. Match the default JPEG quality so auto is lossy.
    if (isNil(options.quality)) {
      params.quality = "85";
    }
  } else {
    params.format = "jpg";
  }

  const width =
    options.width && options.width > 0 ? options.width.toString() : undefined;
  const height =
    options.height && options.height > 0
      ? options.height.toString()
      : undefined;

  const isResizingOrCropping =
    !isNil(options.resizeMethod) || !isNil(options.crop);
  if (isResizingOrCropping) {
    // Only resize and crop needs both width and height
    if (height) {
      params.height = height;
    }
    if (width) {
      params.width = width;
    }
  } else {
    // Otherwise only set one of them and let CDN handle the other dimension
    // based on the original aspect ratio
    if (height) {
      params.height = height;
    } else if (width) {
      params.width = width;
    }
  }

  if (options.resizeMethod) {
    // Default resizeMethod is fit, if you override, you needs to
    // provide both width and height
    if (!width || !height) {
      const error = new Error(
        "getFastlyUrl - resizing requires width and height"
      );
      logger.error(error.message, error, { imageUrl, options });
    }
    if (options.resizeMethod === "fit") {
      params.fit = "bounds";
    } else if (options.resizeMethod === "fill") {
      params.fit = "crop";
    } else if (options.resizeMethod === "pad") {
      params.fit = "bounds";
      params.canvas = `${width}:${height}`;
    }
  }

  if (options.crop) {
    const { width, height, x, y, smartSquare } = options.crop;
    if (smartSquare) {
      params.precrop = "1:1,smart";
      if (!isNil(width) || !isNil(height) || !isNil(x) || !isNil(y)) {
        const error = new Error(
          "getFastlyUrl - crop options includes options that will be overriden"
        );
        logger.error(error.message, error, { imageUrl, options });
      }
    } else if (!isNil(width) && !isNil(height) && !isNil(x) && !isNil(y)) {
      // https://developer.fastly.com/reference/io/crop/
      // CROP_FAIL_SAFE is passed to ensure that the image loads
      // even if the crop param is malformed.
      // If the crop param is malformed the crop will not be applied.
      // Currently we only use manual crop on brand headers,
      // if we use that image anywhere else the manual crop might not be applied.
      // If we want to preserve the manual crop we should look into
      // changing from using fixed crop values to using relative values
      // see: https://github.com/Faire/web-common/pull/222#pullrequestreview-591312668
      const CROP_FAIL_SAFE = "safe";
      if (width > 0 && height > 0) {
        params.precrop = `${width},${height},x${x},y${y},${CROP_FAIL_SAFE}`;
      } else {
        const error = new Error("getFastlyUrl - crop options is malformed");
        logger.error(error.message, error, { imageUrl, options });
      }
    } else {
      const error = new Error("getFastlyUrl - crop options have missing props");
      logger.error(error.message, error, { imageUrl, options });
    }
  }

  if (!isNil(options.quality)) {
    params.quality = `${options.quality}`;
  }

  if (options.background) {
    if (isHexColor(options.background)) {
      const hexColor = options.background.slice(1);
      params["bg-color"] = `${hexColor}`;
    } else {
      const error = new Error("getFastlyUrl - background option is invalid");
      logger.error(error.message, error, { imageUrl, options });
    }
  }

  return `${pathName}?${qs.stringify(params, { encode: false })}`;
};

export const modifyBackendUrl = (
  optimizedUrl: string,
  isSSR: boolean,
  dimensions: { width?: number; height?: number } = {},
  options: IOptimizeImageOptions = {}
): string => {
  const url = new URL(optimizedUrl);
  const dpr = getDpr(options, isSSR);
  const optimizedDimensions = optimizeDimensionsForCacheHits(dimensions, dpr);

  // Only set width or height, but not both, the other dimension will be adjusted to maintain the aspect ratio.
  if (optimizedDimensions.width) {
    url.searchParams.set("width", optimizedDimensions.width.toString());
  } else if (optimizedDimensions.height) {
    url.searchParams.set("height", optimizedDimensions.height.toString());
  }

  if (options.maintainTransparency) {
    url.searchParams.delete("format");
    url.searchParams.delete("bg-color");
  }

  url.searchParams.sort();
  return url.toString();
};

const PORTRAIT_CROP_TAG_PREFIXES = [
  "fastly_3x4:",
  "xp_fastly_3x4:",
  "auto_image_crop_3x4:",
  "manual_image_crop_3x4:",
] as const;

const getPrecropParam = (optimizedUrl: string): string | null => {
  try {
    return new URL(optimizedUrl).searchParams.get("precrop");
  } catch {
    return null;
  }
};

const normalizePrecrop = (value: string): string => {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    decoded = value;
  }
  return decoded.replace(/\s/g, "").replace(/,safe$/i, "");
};

const precropFromPortraitTag = (tag: string): string | undefined => {
  const prefix = PORTRAIT_CROP_TAG_PREFIXES.find((candidate) =>
    tag.startsWith(candidate)
  );
  if (!prefix) {
    return;
  }
  try {
    const crop = JSON.parse(tag.slice(prefix.length)) as {
      preCrop?: string | null;
      width?: number | null;
      height?: number | null;
      x?: number | null;
      y?: number | null;
    };
    if (crop.preCrop) {
      return normalizePrecrop(crop.preCrop);
    }
    if (
      typeof crop.width === "number" &&
      typeof crop.height === "number" &&
      typeof crop.x === "number" &&
      typeof crop.y === "number"
    ) {
      return normalizePrecrop(
        `${crop.width},${crop.height},x${crop.x},y${crop.y}`
      );
    }
  } catch {
    return undefined;
  }
  return undefined;
};

export const isPortraitOptimizedUrl = (image?: Partial<IImage>): boolean => {
  if (!image?.optimized_url) {
    return false;
  }
  const precrop = getPrecropParam(image.optimized_url);
  if (!precrop) {
    return false;
  }
  const normalized = normalizePrecrop(precrop);
  if (normalized === "3:4" || normalized.startsWith("3:4,")) {
    return true;
  }
  return (image.tags ?? []).some(
    (tag) => precropFromPortraitTag(tag) === normalized
  );
};

export const withoutPortraitOptimizedUrl = <T extends Partial<IImage>>(
  image: T
): T => {
  if (!isPortraitOptimizedUrl(image)) {
    return image;
  }
  return { ...image, optimized_url: undefined };
};

export const hasBackendOptimizedUrl = (
  image: Partial<IImage>,
  options: IOptimizeImageOptions = {}
): image is IImage & { optimized_url: string } =>
  !!image?.optimized_url && !options.ignoreResizeTag;

export const shouldUseOptimizedUrl = (
  image: Partial<IImage>,
  options: IOptimizeImageOptions = {}
): image is IImage & { optimized_url: string } =>
  hasBackendOptimizedUrl(image, options) &&
  getPrecropParam(image.optimized_url) !== null;
